"use client";
import { useCallback, useRef, useState } from "react";

type VoiceState = "idle" | "recording" | "transcribing" | "speaking" | "error";

interface UseVoiceReturn {
  state: VoiceState;
  transcript: string | null;
  error: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  speak: (text: string, voiceId?: string | null) => Promise<void>;
}

export function useVoice(): UseVoiceReturn {
  const [state, setState] = useState<VoiceState>("idle");
  const [transcript, setTranscript] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);

  const startRecording = useCallback(async () => {
    setError(null);
    setTranscript(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start(250); // 250ms chunks
      setState("recording");
    } catch (err) {
      setError((err as Error).message);
      setState("error");
    }
  }, []);

  const stopRecording = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    const stream = streamRef.current;
    if (!recorder || recorder.state === "inactive") return;

    setState("transcribing");
    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      recorder.stop();
    });
    stream?.getTracks().forEach((t) => t.stop());

    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    chunksRef.current = [];

    if (blob.size === 0) {
      setError("No audio captured");
      setState("error");
      return;
    }

    try {
      const fd = new FormData();
      fd.append("audio", blob, "input.webm");
      const res = await fetch("/api/voice/stt", { method: "POST", body: fd });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.error || `STT ${res.status}`);
      }
      const { transcript: text } = (await res.json()) as { transcript: string };
      setTranscript(text);
      setState("idle");
    } catch (err) {
      setError((err as Error).message);
      setState("error");
    }
  }, []);

  const speak = useCallback(async (text: string, voiceId?: string | null) => {
    setError(null);
    setState("speaking");
    try {
      const res = await fetch("/api/voice/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, voiceId: voiceId ?? null }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.error || `TTS ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioElRef.current = audio;
      await new Promise<void>((resolve, reject) => {
        audio.onended = () => {
          URL.revokeObjectURL(url);
          resolve();
        };
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("Audio playback failed"));
        };
        audio.play().catch(reject);
      });
      setState("idle");
    } catch (err) {
      setError((err as Error).message);
      setState("error");
    }
  }, []);

  return { state, transcript, error, startRecording, stopRecording, speak };
}
